import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import {
  ArrowLeft, Phone, MessageCircle, Mail, Plus, AlertTriangle, Loader2,
  MoreVertical, X, Pencil, BookOpen, Trash2, Plane,
} from 'lucide-react';
import { cachedGet, invalidate } from '../lib/cache';
import { Avatar } from '../components/Avatar';
import { TripCard } from '../components/TripCard';
import { inr, waNumber } from '../lib/ui';
import { useAuth } from '@/contexts/AuthContext';
import TravellerDocuments from '@/components/traveller/TravellerDocuments';
import SensitivePii from '@/components/traveller/SensitivePii';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fmtDate = (s) => { if (!s) return '—'; const d = new Date(s); return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); };
const dateOf = (s) => { if (!s) return null; const d = new Date(s); return isNaN(d.getTime()) ? null : d; };

export const MobileTravellerProfile = () => {
  const { ucc } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const role = (user?.role || '').toLowerCase();
  const [t, setT] = useState(null);
  const [stats, setStats] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    Promise.all([
      cachedGet(`/api/clients/${ucc}`),
      cachedGet(`/api/clients/${ucc}/stats`).catch(() => null),
    ]).then(([c, s]) => { if (alive) { setT(c); setStats(s); } })
      .catch(() => { toast.error('Could not load traveller'); navigate(-1); });
    return () => { alive = false; };
  }, [ucc, navigate]);

  const expiryDays = useMemo(() => {
    if (!t?.passportExpiryDate) return null;
    const d = new Date(t.passportExpiryDate);
    if (isNaN(d.getTime())) return null;
    return Math.floor((d.getTime() - Date.now()) / 86400000);
  }, [t]);

  const { upcoming, history } = useMemo(() => {
    const trips = (t?.trips || t?.quotes || []);
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const up = []; const hist = [];
    trips.forEach((tr) => {
      const d = dateOf(tr.travelStartDate || tr.departureDate);
      const active = (tr.status || '').toLowerCase() !== 'cancelled';
      if (d && d >= today && active) up.push(tr); else hist.push(tr);
    });
    up.sort((a, b) => (dateOf(a.travelStartDate) || 0) - (dateOf(b.travelStartDate) || 0));
    hist.sort((a, b) => new Date(b.updatedAt || b.createdAt || 0) - new Date(a.updatedAt || a.createdAt || 0));
    return { upcoming: up, history: hist };
  }, [t]);

  if (!t) {
    return <div className="fixed inset-0 bg-white flex items-center justify-center" data-testid="m-traveller-loading"><Loader2 className="h-6 w-6 animate-spin text-[#0F6E56]" /></div>;
  }

  const wa = waNumber(t.countryCode, t.phone);
  const meta = [t.displayUCC || t.ucc, t.travelCategory].filter(Boolean).join(' · ');
  const ffps = t.frequentFlyers || [];
  const family = t.familyMembers || [];

  const del = async () => {
    setBusy(true);
    try {
      await axios.delete(`${BACKEND_URL}/api/clients/${ucc}`, { withCredentials: true });
      invalidate('/api/clients');
      toast.success('Traveller deleted');
      navigate(-1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    } finally { setBusy(false); }
  };

  const closeMenu = () => { setMenuOpen(false); setConfirmDel(false); };

  return (
    <div className="fixed inset-0 bg-[#F8F9FA] flex flex-col" data-testid="m-traveller-profile">
      <header className="h-[52px] bg-white border-b border-[#E5E7EB] flex items-center gap-2 px-2 shrink-0">
        <button type="button" onClick={() => navigate(-1)} data-testid="m-traveller-back" aria-label="Back" className="w-11 h-11 flex items-center justify-center text-[#111827]"><ArrowLeft className="h-5 w-5" /></button>
        <p className="text-[15px] font-semibold text-[#111827] truncate flex-1">{t.fullName}</p>
        <button type="button" onClick={() => setMenuOpen(true)} data-testid="m-traveller-menu-btn" aria-label="More actions" className="w-11 h-11 flex items-center justify-center text-[#111827]"><MoreVertical className="h-5 w-5" /></button>
      </header>

      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3" style={{ paddingBottom: 'calc(24px + env(safe-area-inset-bottom))' }}>
        {/* 1 — Profile card (email wraps) */}
        <div className="bg-white border border-[#E5E7EB] rounded-xl p-4" data-testid="m-traveller-card">
          <div className="flex items-center gap-3">
            <Avatar name={t.fullName} src={t.picture} size={52} />
            <div className="min-w-0 flex-1">
              <p className="text-[17px] font-semibold text-[#111827] leading-tight">{t.fullName}</p>
              <p className="text-[12px] text-[#6B7280] mt-0.5">{meta}</p>
            </div>
          </div>
          {t.email && <a href={`mailto:${t.email}`} data-testid="m-traveller-email-text" className="block mt-2.5 text-[13px] text-[#0F6E56] break-words leading-snug">{t.email}</a>}
          {t.phone && <a href={`tel:${t.countryCode || ''}${t.phone}`} data-testid="m-traveller-phone" className="mt-1.5 inline-flex items-center gap-1.5 text-[13px] text-[#0F6E56]"><Phone className="h-3.5 w-3.5" /> {t.countryCode || ''} {t.phone}</a>}
        </div>

        {/* 2 — Passport expiry warning banner */}
        {expiryDays !== null && expiryDays < 90 && (
          <div className={`rounded-xl p-3 flex items-start gap-2.5 border ${expiryDays < 0 ? 'bg-rose-50 border-rose-200 text-rose-700' : 'bg-amber-50 border-amber-200 text-amber-700'}`} data-testid="m-passport-banner">
            <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" />
            <div className="min-w-0">
              <p className="text-[13px] font-semibold">{expiryDays < 0 ? 'Passport has expired' : `Passport expires in ${expiryDays} day${expiryDays === 1 ? '' : 's'}`}</p>
              <p className="text-[11px] opacity-80">Expiry {fmtDate(t.passportExpiryDate)} · renew before international travel.</p>
            </div>
          </div>
        )}

        {/* 3 — Stats row */}
        <div className="grid grid-cols-4 gap-2" data-testid="m-traveller-stats">
          <Stat label="LTV" value={inr(stats?.ltv)} />
          <Stat label="Trips" value={stats?.totalTrips ?? 0} />
          <Stat label="Last trip" value={fmtDate(stats?.lastTripDate)} small />
          <Stat label="Avg value" value={inr(stats?.avgTripValue)} small />
        </div>

        {/* 4 — Action row */}
        <div className="grid grid-cols-4 gap-2" data-testid="m-traveller-actions">
          <ActBtn icon={Phone} label="Call" testid="m-traveller-call" onClick={() => t.phone ? (window.location.href = `tel:${t.countryCode || ''}${t.phone}`) : toast.error('No phone')} />
          <ActBtn icon={MessageCircle} label="WhatsApp" tone testid="m-traveller-wa" onClick={() => t.phone ? window.open(`https://wa.me/${wa}`, '_blank', 'noopener') : toast.error('No phone')} />
          <ActBtn icon={Mail} label="Email" testid="m-traveller-email" onClick={() => t.email ? (window.location.href = `mailto:${t.email}`) : toast.error('No email')} />
          <ActBtn icon={Plus} label="Trip" tone testid="m-traveller-trip" onClick={() => navigate(`/quotes/new?ucc=${t.ucc}`)} />
        </div>

        {/* 5 — Travel info */}
        <Section title="Travel info" testid="m-traveller-travel-info">
          <InfoRow label="Nationality" value={t.nationality} />
          <InfoRow label="Passport expiry" value={t.passportExpiryDate ? fmtDate(t.passportExpiryDate) : ''} />
          <InfoRow label="Date of birth" value={t.dateOfBirth ? fmtDate(t.dateOfBirth) : ''} />
          <div className="pt-2 mt-1 border-t border-[#F1F1F1]">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-[#6B7280] mb-1.5">Frequent flyer</p>
            {ffps.length > 0 ? ffps.map((f, i) => (
              <div key={i} className="flex items-center justify-between py-1 text-[13px]">
                <span className="text-[#111827] font-medium truncate pr-2">{f.airline || 'Airline'}</span>
                <span className="text-[#6B7280] shrink-0">{[f.code, f.tier].filter(Boolean).join(' · ') || '—'}</span>
              </div>
            )) : <p className="text-[12px] text-[#6B7280]">No frequent-flyer programs</p>}
          </div>
        </Section>

        {/* 5b — Sensitive identity fields (PAN / Passport / Aadhaar) — BATCH 1 mask+reveal */}
        <SensitivePii
          ucc={ucc}
          traveller={t}
          trips={t.trips || t.quotes || []}
          role={role}
          showGrantManager={false}
        />

        {/* 6 — Emergency contact */}
        <Section title="Emergency contact" testid="m-traveller-emergency">
          {(t.emergencyContactName || t.emergencyContactPhone) ? (
            <>
              <InfoRow label="Name" value={t.emergencyContactName} />
              <InfoRow label="Relation" value={t.emergencyContactRelation} />
              <InfoRow label="Phone" value={t.emergencyContactPhone} href={t.emergencyContactPhone ? `tel:${t.emergencyContactPhone}` : null} />
            </>
          ) : <p className="text-[12px] text-[#6B7280]">No emergency contact on file.</p>}
        </Section>

        {/* 7 — Upcoming trips */}
        <Section title={`Upcoming trips${upcoming.length ? ` (${upcoming.length})` : ''}`} testid="m-traveller-upcoming">
          {upcoming.length > 0 ? (
            <div className="space-y-2.5">{upcoming.map((tr) => <TripCard key={tr.id} trip={tr} />)}</div>
          ) : (
            <div className="flex items-center gap-2 text-[12px] text-[#6B7280]"><Plane className="h-4 w-4" /> No upcoming trips.</div>
          )}
        </Section>

        {/* 8 — Family members */}
        <Section title={`Family members${family.length ? ` (${family.length})` : ''}`} testid="m-traveller-family">
          {family.length > 0 ? family.map((m, i) => (
            <div key={i} className="flex items-center gap-3 py-2 border-b border-[#F1F1F1] last:border-0 last:pb-0">
              <Avatar name={m.fullName} size={36} />
              <div className="min-w-0 flex-1">
                <p className="text-[13px] font-medium text-[#111827] truncate">{m.fullName || 'Family member'}</p>
                <p className="text-[11px] text-[#6B7280]">{[m.relationship, m.dateOfBirth ? fmtDate(m.dateOfBirth) : null].filter(Boolean).join(' · ') || '—'}</p>
              </div>
              {m.passportNumber && <span className="shrink-0 text-[10px] font-semibold text-[#0F6E56] bg-[#E7F1ED] px-2 py-0.5 rounded-full">Passport</span>}
            </div>
          )) : <p className="text-[12px] text-[#6B7280]">No family members added.</p>}
        </Section>

        {/* 9 — Documents */}
        <TravellerDocuments ucc={ucc} onAutofill={() => {}} />

        {/* 10 — Trip history */}
        <Section title={`Trip history${history.length ? ` (${history.length})` : ''}`} testid="m-traveller-history">
          {history.length > 0 ? (
            <div className="space-y-2.5">{history.map((tr) => <TripCard key={tr.id} trip={tr} />)}</div>
          ) : (
            <p className="text-[12px] text-[#6B7280]">No past trips yet.</p>
          )}
        </Section>
      </div>

      {/* Top-right action sheet */}
      {menuOpen && (
        <>
          <button type="button" aria-label="Close menu" onClick={closeMenu} className="fixed inset-0 z-[55] bg-black/30" />
          <div className="fixed inset-x-0 bottom-0 z-[56] bg-white rounded-t-2xl border-t border-[#E5E7EB] p-3" style={{ paddingBottom: 'calc(12px + env(safe-area-inset-bottom))' }} data-testid="m-traveller-menu-sheet">
            <div className="flex items-center justify-between px-1 pb-2">
              <p className="text-[14px] font-semibold text-[#111827] truncate">{confirmDel ? 'Delete traveller?' : t.fullName}</p>
              <button type="button" onClick={closeMenu} aria-label="Close" className="w-9 h-9 flex items-center justify-center text-[#6B7280]"><X className="h-5 w-5" /></button>
            </div>
            {confirmDel ? (
              <div className="px-1">
                <p className="text-[13px] text-[#6B7280] mb-3">This removes <span className="font-medium text-[#111827]">{t.fullName}</span> from the travellers list. This cannot be undone here.</p>
                <div className="grid grid-cols-2 gap-2.5">
                  <button type="button" onClick={() => setConfirmDel(false)} className="h-11 rounded-lg border border-[#E5E7EB] text-[#111827] text-[14px] font-medium">Cancel</button>
                  <button type="button" onClick={del} disabled={busy} data-testid="m-traveller-delete-confirm" className="h-11 rounded-lg bg-rose-600 text-white text-[14px] font-medium flex items-center justify-center gap-1.5">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Trash2 className="h-4 w-4" /> Delete</>}
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-0.5">
                <MenuItem icon={Pencil} label="Edit traveller" testid="m-traveller-menu-edit" onClick={() => { closeMenu(); navigate(`/clients/${ucc}/edit`); }} />
                <MenuItem icon={Plus} label="Add trip" testid="m-traveller-menu-trip" onClick={() => { closeMenu(); navigate(`/quotes/new?ucc=${t.ucc}`); }} />
                <MenuItem icon={BookOpen} label="View ledger" testid="m-traveller-menu-ledger" onClick={() => { closeMenu(); navigate(`/ledger?party_type=traveller&party_id=${ucc}`); }} />
                {isAdmin && <MenuItem icon={Trash2} label="Delete traveller" danger testid="m-traveller-menu-delete" onClick={() => setConfirmDel(true)} />}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

const ActBtn = ({ icon: Icon, label, onClick, tone, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className="h-11 rounded-lg border border-[#E5E7EB] flex flex-col items-center justify-center gap-0.5"
    style={{ color: tone ? '#0F6E56' : '#111827' }}>
    <Icon className="h-[18px] w-[18px]" />
    <span className="text-[10px] font-medium">{label}</span>
  </button>
);

const Stat = ({ label, value, small }) => (
  <div className="bg-white border border-[#E5E7EB] rounded-xl px-2 py-2.5 text-center">
    <p className={`font-bold text-[#111827] ${small ? 'text-[12px]' : 'text-[15px]'} leading-tight`}>{value}</p>
    <p className="text-[10px] text-[#6B7280] mt-0.5">{label}</p>
  </div>
);

const Section = ({ title, children, testid }) => (
  <div className="bg-white border border-[#E5E7EB] rounded-xl p-4" data-testid={testid}>
    <h2 className="text-[11px] font-semibold uppercase tracking-wide text-[#6B7280] mb-2.5">{title}</h2>
    {children}
  </div>
);

const InfoRow = ({ label, value, href }) => (
  <div className="flex items-start justify-between py-1.5 gap-3">
    <span className="text-[12px] text-[#6B7280] shrink-0">{label}</span>
    {href && value
      ? <a href={href} className="text-[13px] font-medium text-[#0F6E56] text-right break-words">{value}</a>
      : <span className="text-[13px] font-medium text-[#111827] text-right break-words">{value || '—'}</span>}
  </div>
);

const MenuItem = ({ icon: Icon, label, onClick, danger, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className="w-full h-12 flex items-center gap-3 px-2 rounded-lg active:bg-[#F8F9FA] text-left"
    style={{ color: danger ? '#e11d48' : '#111827' }}>
    <Icon className="h-5 w-5 shrink-0" />
    <span className="text-[15px] font-medium">{label}</span>
  </button>
);

export default MobileTravellerProfile;
